import {useEffect, useState} from 'react'
import {MainBox, StyledTitle, StyledTitleDate, StyledDate, StyledContainer} from './AdminPanelStyling'
import GreenBarLine from './common/GreenBarLine'
import PropTypes from 'prop-types'
import NotificationBanner from '../components/common/NotificationBanner/NotificationBanner'
import {useExpiringUser} from '../store/hooks/expiringUser'
import {useNotificationBanner} from '../store/hooks/notificationBanner'
import {Box} from '@mui/material'
import {useAnalyticsPageLoad} from '../hooks/useAnalyticsPageLoad'
import {useRolePermissions} from '../hooks/useRolesPermissions'
import {useNavigate, useParams} from 'react-router-dom'
import Tabs from './common/Tab/TabsComponent'
import ManageClientPermissions from './ManageClientPermissions/ManageClientPermissions'
import ManageMyPermissions from './ManageMyPermissions/ManageMyPermissions'
import ViewChangeLogs from './AdministrationTabs/LogChanges/ViewChangeLogs'

const getDataRefreshDate = () => {
  return process.env.NODE_ENV === 'production'
    ? globalThis.__env.REACT_APP_ADMIN_PANEL_DATA_REFRESH_DATE
    : process.env.REACT_APP_ADMIN_PANEL_DATA_REFRESH_DATE
}

/**
 * tabsConfig: Configuration for the tabs in the admin panel, including the component to render, label, value, and title for each tab.
 * To reuse this component for other admin panels, simply update the tabsConfig array with the appropriate components and labels for the new context.
 * Each object in the tabsConfig array represents a tab, where:
 * - component: The React component to render when the tab is active.
 * - label: The text label for the tab.
 * - value: A unique identifier for the tab (used for state management).
 * - title: The title displayed at the top of the tab panel when the tab is active.
 */
const tabsConfig = [
  {
    component: <ManageClientPermissions />,
    label: 'Manage Client Permissions',
    value: 'manage-client-permissions'
  },
  {
    component: <ViewChangeLogs />,
    label: 'View Change Logs',
    value: 'view-change-logs'
  },
  {
    component: <ManageMyPermissions />,
    label: 'Manage My Permissions',
    value: 'manage-my-permissions'
  }
]

const AdminPanel = ({setIsAdminPanelOpen}) => {
  useEffect(() => {
    setIsAdminPanelOpen(true)
  }, [setIsAdminPanelOpen])

  const [showBanner, setShowBanner] = useState(false)
  const navigate = useNavigate()
  const {tab} = useParams()
  const {adminBanner, updateAdminBanner} = useNotificationBanner()
  const {isSuperAdmin, isDeliveryAdmin, isCustomerSupport} = useRolePermissions()
  const shouldShowManageMyPermissions = isSuperAdmin() || isCustomerSupport()

  const visibleTabsConfig = shouldShowManageMyPermissions
    ? tabsConfig
    : tabsConfig.filter((config) => config.value !== 'manage-my-permissions')

  const initialTabIndex = visibleTabsConfig.findIndex((config) => config.value === tab)

  useEffect(() => {
    setShowBanner(isSuperAdmin() || isDeliveryAdmin() || isCustomerSupport())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAdminBannerClose = () => {
    updateAdminBanner(false)
  }

  const handleTabUrlSync = (newTabIndex) => {
    const selectedTab = visibleTabsConfig[newTabIndex]
    if (!selectedTab || tab === selectedTab.value) {
      return
    }
    navigate(`/adminpanel/${selectedTab.value}`, {replace: true})
  }

  useAnalyticsPageLoad('Administration')

  const {data: expiringUserData} = useExpiringUser()

  return (
    <MainBox component='main'>
      {expiringUserData?.expiringUsersList?.UsersDetails.length > 0 && (
        <NotificationBanner
          isAdminpanelOpen={showBanner}
          message={'The following user(s) have trials expiring soon:'}
          hasList={true}
          list={expiringUserData?.expiringUsersList?.UsersDetails}
          open={adminBanner}
          ariaLabel='The following user/users have trials expiring soon'
          handleAdminBannerClose={handleAdminBannerClose}
        />
      )}
      <StyledContainer>
        <Box display='flex' flexDirection='row' alignItems='center'>
          <StyledTitle variant='h5' tabIndex={0} aria-label='Administration home page' title={'administration'}>
            Administration
          </StyledTitle>
          <Box flexGrow={1} />
          <Box display='flex' alignItems='center'>
            <StyledTitleDate variant='subtitle1' tabIndex={0} aria-label='data last refreshed'>
              Data Brick:
            </StyledTitleDate>
            <StyledDate variant='subtitle1' tabIndex={0}>
              {getDataRefreshDate()}
            </StyledDate>
          </Box>
        </Box>
        <GreenBarLine title={'administration'} />
        <br />
        <Tabs
          tabsConfig={visibleTabsConfig}
          ariaLabel='Admin Panel Tabs'
          initialTabIndex={Math.max(initialTabIndex, 0)}
          onTabChange={handleTabUrlSync}
        />
      </StyledContainer>
    </MainBox>
  )
}

AdminPanel.propTypes = {
  setIsAdminPanelOpen: PropTypes.func
}

export default AdminPanel

/* Function Descriptions:
  - getDataRefreshDate: Retrieves the date when the data was last refreshed, based on the environment.
  - AdminPanel: Main component that renders the admin panel UI, including a list of clients and an option to create a new client.
    - useEffect 1: Sets the admin panel open state on component mount.
    - useEffect 2: Determines user roles and sets admin privileges and banner visibility.
    - handleAdminBannerClose: Closes the admin notification banner.
    - useEffect 3: Tracks page load analytics for the Administration page.
*/
